import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { AuthService } from './core/auth.service';
import { CartService } from './core/cart.service';
import { CatalogFacade } from './core/catalog.facade';
import { CompareService } from './core/compare.service';
import { LanguageService } from './core/language.service';
import { PriceSseService } from './core/price-sse.service';
import { ToastService } from './core/toast.service';

import { ChatWidgetComponent } from './shared/chat-widget.component';
import { CompareBarComponent } from './shared/compare-bar.component';

@Component({
  selector: 'app-root',
  imports: [
    FormsModule,
    RouterLink,
    RouterOutlet,
    TranslatePipe,
    CompareBarComponent,
    ChatWidgetComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly router = inject(Router);
  private readonly catalog = inject(CatalogFacade);

  protected readonly cart = inject(CartService);
  protected readonly auth = inject(AuthService);
  protected readonly language = inject(LanguageService);
  protected readonly toast = inject(ToastService);
  protected readonly compare = inject(CompareService);
  protected readonly categories = toSignal(this.catalog.categories(), { initialValue: [] });
  protected readonly searchTerm = signal('');
  protected readonly authPage = signal(this.router.url.startsWith('/auth'));
  protected readonly menuOpen = signal(false);
  private readonly catScroll = viewChild<ElementRef<HTMLElement>>('catScroll');

  constructor() {
    const priceSse = inject(PriceSseService);
    afterNextRender(() => priceSse.connect());
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((event) => {
        this.authPage.set(event.urlAfterRedirects.startsWith('/auth'));
        // Always dismiss the mobile drawer after a navigation so it never lingers over a new page.
        this.menuOpen.set(false);
      });
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected search(): void {
    const query = this.searchTerm().trim();
    void this.router.navigate(['/search'], { queryParams: query ? { q: query } : {} });
  }

  protected scrollCats(delta: number): void {
    this.catScroll()?.nativeElement.scrollBy({ left: delta, behavior: 'smooth' });
  }
}
